"use client";

import * as React from "react";

import {
  DateRange,
  DayButton,
  DayPicker,
  getDefaultClassNames,
} from "react-day-picker";

import { cn } from "./lib/utils";
import { Button, buttonVariants } from "./button";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import dayjs from "dayjs";

function ComparisonCalendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  buttonVariant = "ghost",
  formatters,
  components,

  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>["variant"];
  selected?: DateRange[] | DateRange;
}) {
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        "group/calendar p-3 [--cell-size:2rem] in-data-[slot=card-content]:bg-transparent in-data-[slot=popover-content]:bg-transparent",
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className
      )}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString("default", { month: "short" }),
        ...formatters,
      }}
      classNames={{
        root: cn("w-fit", defaultClassNames.root),
        months: cn(
          "relative flex flex-col gap-4 md:flex-row",
          defaultClassNames.months
        ),
        month: cn("flex w-full flex-col gap-4", defaultClassNames.month),
        nav: cn(
          "absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1",
          defaultClassNames.nav
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          "h-(--cell-size) w-(--cell-size) select-none p-0 aria-disabled:opacity-50",
          defaultClassNames.button_previous
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          "h-(--cell-size) w-(--cell-size) select-none p-0 aria-disabled:opacity-50",
          defaultClassNames.button_next
        ),
        month_caption: cn(
          "flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)",
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          "flex h-(--cell-size) w-full items-center justify-center gap-1.5 text-sm font-medium",
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn(
          "has-focus:border-ring border-input shadow-xs has-focus:ring-ring/50 has-focus:ring-[3px] relative rounded-full border",
          defaultClassNames.dropdown_root
        ),
        dropdown: cn("absolute inset-0 opacity-0", defaultClassNames.dropdown),
        caption_label: cn(
          "select-none font-medium",
          captionLayout === "label"
            ? "text-sm"
            : "[&>svg]:text-muted-foreground flex h-8 items-center gap-1 rounded-full pl-2 pr-1 text-sm [&>svg]:size-3.5",
          defaultClassNames.caption_label
        ),
        table: "w-full border-collapse",
        weekdays: cn("flex", defaultClassNames.weekdays),
        weekday: cn(
          "text-muted-foreground flex-1 select-none rounded-full text-[0.8rem] font-normal",
          defaultClassNames.weekday
        ),
        week: cn("mt-2 flex w-full", defaultClassNames.week),
        week_number_header: cn(
          "w-(--cell-size) select-none",
          defaultClassNames.week_number_header
        ),
        week_number: cn(
          "text-muted-foreground select-none text-[0.8rem]",
          defaultClassNames.week_number
        ),
        day: cn(
          "group/day relative aspect-square h-full w-full select-none p-0 text-center",
          defaultClassNames.day
        ),
        range_start: cn(defaultClassNames.range_start),
        range_middle: cn("rounded-none", defaultClassNames.range_middle),
        range_end: cn(defaultClassNames.range_end),
        today: cn(
          "bg-primary text-primary-foreground rounded-full data-[selected=true]:rounded-none",
          defaultClassNames.today
        ),
        outside: cn(
          "text-muted-foreground aria-selected:text-muted-foreground",
          defaultClassNames.outside
        ),
        disabled: cn(
          "text-muted-foreground opacity-50",
          defaultClassNames.disabled
        ),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn(className)}
              {...props}
            />
          );
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === "left") {
            return (
              <ChevronLeftIcon className={cn("size-4", className)} {...props} />
            );
          }

          if (orientation === "right") {
            return (
              <ChevronRightIcon
                className={cn("size-4", className)}
                {...props}
              />
            );
          }

          return (
            <ChevronDownIcon className={cn("size-4", className)} {...props} />
          );
        },
        DayButton: ComparisonCalendarDayButton,
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="flex size-(--cell-size) items-center justify-center text-center">
                {children}
              </div>
            </td>
          );
        },
        ...components,
      }}
      {...props}
    />
  );
}

function ComparisonCalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames();

  const ref = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  // Check for our custom range modifiers
  const isCurrentRangeStart = modifiers.current_range_start;
  const isCurrentRangeEnd = modifiers.current_range_end;
  const isCurrentRangeMiddle =
    modifiers.current_range_middle ||
    modifiers.current_range_middle_after ||
    modifiers.current_range_middle_before_overlap ||
    modifiers.current_range_middle_after_overlap;

  const isComparisonRangeStart = modifiers.comparison_range_start;
  const isComparisonRangeEnd = modifiers.comparison_range_end;
  const isComparisonRangeMiddle =
    modifiers.comparison_range_middle ||
    modifiers.comparison_range_middle_after ||
    modifiers.comparison_range_middle_before_overlap ||
    modifiers.comparison_range_middle_after_overlap;

  const isOverlapStart = modifiers.overlap_start;
  const isOverlapEnd = modifiers.overlap_end;
  const isOverlapMiddle = modifiers.overlap_middle;

  // Determine styling priority: ABSOLUTE overlap priority
  let buttonStyles = "";
  // Overlap takes ABSOLUTE priority - check this first and ONLY this
  if (isOverlapStart || isOverlapEnd || isOverlapMiddle) {
    if (isOverlapStart && isOverlapEnd) {
      // Single day overlap
      buttonStyles += " bg-cyan-500! text-white rounded-full";
    } else if (isOverlapStart) {
      buttonStyles += " bg-cyan-500! text-white rounded-l-full";
    } else if (isOverlapEnd) {
      buttonStyles += " bg-cyan-500! text-white rounded-r-full";
    } else if (isOverlapMiddle) {
      buttonStyles += " bg-cyan-500/50! text-white rounded-none!";
    }
  }
  // Only check other ranges if NO overlap modifiers

  if (isComparisonRangeStart) {
    buttonStyles += " bg-purple-500! text-white rounded-l-full";
  }
  if (isComparisonRangeEnd) {
    buttonStyles += " bg-purple-500! text-white rounded-r-full";
  }
  if (isComparisonRangeMiddle) {
    buttonStyles += " bg-purple-500/50! text-white rounded-none!";
  }
  if (isCurrentRangeStart) {
    buttonStyles += " !bg-primary text-primary-foreground rounded-l-full";
  }
  if (isCurrentRangeEnd) {
    buttonStyles += " !bg-primary text-primary-foreground rounded-r-full";
  }
  if (isCurrentRangeMiddle) {
    buttonStyles += " !bg-primary/50 text-primary-foreground rounded-none!";
  }

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon-sm"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle &&
        !isCurrentRangeStart &&
        !isCurrentRangeEnd &&
        !isCurrentRangeMiddle &&
        !isComparisonRangeStart &&
        !isComparisonRangeEnd &&
        !isComparisonRangeMiddle &&
        !isOverlapStart &&
        !isOverlapEnd &&
        !isOverlapMiddle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        // Base styles
        "flex aspect-square h-auto w-full min-w-(--cell-size) flex-col gap-1 font-normal leading-none",
        "[&>span]:text-xs [&>span]:opacity-70",

        // Default single selection styling (when no custom ranges apply)
        !buttonStyles &&
          "data-[selected-single=true]:bg-primary rounded-full data-[selected-single=true]:text-primary-foreground",

        // Apply our custom range styles
        buttonStyles,

        defaultClassNames.day,
        className
      )}
      {...props}
    />
  );
}

export { ComparisonCalendar, ComparisonCalendarDayButton };
